/**
 * Locations administration page.
 */

import { useState } from "react";
import { useLocations, useDeleteLocation } from "@/api/hooks";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import type { Location, LocationType } from "@/api/types";
import {
  Plus,
  Loader2,
  AlertCircle,
  Pencil,
  Trash2,
  Building2,
  Network,
  Home,
  Layers,
  ChevronRight,
} from "lucide-react";
import { LocationDialog } from "../components/locations/LocationDialog";
import { LOCATION_TYPE_LABELS } from "../locationForm";

const typeIcons: Record<LocationType, React.ElementType> = {
  network: Network,
  library: Building2,
  branch: Home,
  section: Layers,
};

const typeColors: Record<LocationType, string> = {
  network: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
  library: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  branch: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  section: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300",
};

export function LocationsPage() {
  const { data: locations, isLoading, error } = useLocations();
  const deleteLocation = useDeleteLocation();
  const { isSuperAdmin } = useAuth();
  // undefined: closed, null: creating, a location: editing it
  const [editing, setEditing] = useState<Location | null | undefined>(undefined);

  // Build tree structure from flat list
  const buildTree = (locations: Location[]): Location[] => {
    const roots = locations.filter((l) => !l.parentId);
    return roots.sort((a, b) => a.name.localeCompare(b.name));
  };

  const getChildren = (locations: Location[], parentId: string): Location[] => {
    return locations
      .filter((l) => l.parentId === parentId)
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const handleDelete = async (location: Location) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer "${location.name}" ?`)) {
      try {
        await deleteLocation.mutateAsync(location.id);
      } catch {
        // Error handled by mutation
      }
    }
  };

  const renderLocationRow = (location: Location, depth: number = 0): React.ReactNode => {
    const TypeIcon = typeIcons[location.type];
    const children = locations ? getChildren(locations, location.id) : [];

    return (
      <>
        <tr key={location.id} className="hover:bg-muted/25 border-b last:border-0">
          <td className="px-4 py-3">
            <div
              className="flex items-center gap-2"
              style={{ paddingLeft: `${String(depth * 24)}px` }}
            >
              {depth > 0 && <ChevronRight className="text-muted-foreground h-4 w-4" />}
              <TypeIcon className="text-muted-foreground h-5 w-5" />
              <div>
                <p className="font-medium">{location.name}</p>
                <p className="text-muted-foreground text-sm">{location.code}</p>
              </div>
            </div>
          </td>
          <td className="px-4 py-3">
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${typeColors[location.type]}`}
            >
              {LOCATION_TYPE_LABELS[location.type]}
            </span>
          </td>
          <td className="px-4 py-3">
            {location.contact?.email && <p className="text-sm">{location.contact.email}</p>}
            {location.contact?.phone && (
              <p className="text-muted-foreground text-sm">{location.contact.phone}</p>
            )}
            {!location.contact?.email && !location.contact?.phone && (
              <span className="text-muted-foreground text-sm">—</span>
            )}
          </td>
          <td className="px-4 py-3">
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                location.active
                  ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                  : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
              }`}
            >
              {location.active ? "Actif" : "Inactif"}
            </span>
          </td>
          <td className="px-4 py-3 text-right">
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Modifier ${location.name}`}
                onClick={() => {
                  setEditing(location);
                }}
              >
                <Pencil className="h-4 w-4" aria-hidden />
              </Button>
              {isSuperAdmin && (
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Supprimer ${location.name}`}
                  onClick={() => handleDelete(location)}
                  disabled={deleteLocation.isPending}
                >
                  <Trash2 className="text-destructive h-4 w-4" aria-hidden />
                </Button>
              )}
            </div>
          </td>
        </tr>
        {children.map((child) => renderLocationRow(child, depth + 1))}
      </>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Établissements</h1>
          <p className="text-muted-foreground">
            Gérez la hiérarchie des bibliothèques et leurs antennes
          </p>
        </div>
        {isSuperAdmin && (
          <Button
            onClick={() => {
              setEditing(null);
            }}
          >
            <Plus className="mr-2 h-4 w-4" aria-hidden />
            Nouvel établissement
          </Button>
        )}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : error ? (
        <div className="text-destructive flex flex-col items-center justify-center py-12">
          <AlertCircle className="mb-4 h-12 w-12" />
          <p>Erreur lors du chargement des établissements</p>
        </div>
      ) : (
        <div className="rounded-lg border">
          <table className="w-full">
            <thead>
              <tr className="bg-muted/50 border-b">
                <th className="px-4 py-3 text-left text-sm font-medium">Établissement</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Type</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Contact</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Statut</th>
                <th className="px-4 py-3 text-right text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {locations && buildTree(locations).map((location) => renderLocationRow(location))}
            </tbody>
          </table>

          {locations?.length === 0 && (
            <div className="text-muted-foreground flex flex-col items-center justify-center py-12">
              <Building2 className="mb-4 h-12 w-12" />
              <p>Aucun établissement trouvé</p>
            </div>
          )}
        </div>
      )}

      {editing !== undefined && (
        <LocationDialog
          locations={locations ?? []}
          location={editing}
          onClose={() => {
            setEditing(undefined);
          }}
          onSaved={() => {
            setEditing(undefined);
          }}
        />
      )}
    </div>
  );
}
